import { createContext, useContext, useState, useCallback, useEffect } from "react";

// Default translations - English is the base
const DEFAULT_TRANSLATIONS = {
  en: {
    // Navigation & Layout
    "nav.dashboard": "Dashboard",
    "nav.api_keys": "API Keys",
    "nav.analytics": "Analytics",
    "nav.billing": "Billing & Plans",
    "nav.api_docs": "API Documentation",
    "nav.settings": "Settings",
    "nav.help": "Help & Support",
    "nav.admin_portal": "Admin Portal",
    "nav.customer_portal": "Customer Portal",
    "nav.system_overview": "System Overview",
    "nav.device_management": "Device Management",
    "nav.tenant_management": "Tenant Management",
    "nav.audit_logs": "Audit Logs",
    "nav.alerts": "Alerts",
    "nav.widget_builder": "Widget Builder",
    "nav.live_dashboard": "Live Dashboard",
    "nav.role_management": "Role Management",
    "nav.translations": "Translations",
    "nav.report_builder": "Report Builder",
    "nav.reports": "Reports",
    "nav.data_archival": "Data Archival",
    "nav.plan_management": "Plan Management",
    "nav.email_config": "Email Configuration",
    "nav.security": "Security",
    
    // Common Actions
    "action.save": "Save",
    "action.cancel": "Cancel",
    "action.delete": "Delete",
    "action.edit": "Edit",
    "action.create": "Create",
    "action.search": "Search",
    "action.filter": "Filter",
    "action.export": "Export",
    "action.import": "Import",
    "action.upload": "Upload",
    "action.download": "Download",
    "action.submit": "Submit",
    "action.next": "Next",
    "action.previous": "Previous",
    "action.back": "Back",
    "action.close": "Close",
    "action.confirm": "Confirm",
    "action.add": "Add",
    "action.remove": "Remove",
    "action.view": "View",
    "action.copy": "Copy",
    "action.duplicate": "Duplicate",
    "action.refresh": "Refresh",
    "action.run": "Run",
    "action.schedule": "Schedule",
    "action.archive": "Archive",
    "action.restore": "Restore",
    "action.request": "Request",
    "action.login": "Sign In",
    "action.logout": "Sign Out",
    "action.register": "Register",
    
    // Common Labels
    "label.name": "Name",
    "label.email": "Email",
    "label.password": "Password",
    "label.description": "Description",
    "label.status": "Status",
    "label.type": "Type",
    "label.date": "Date",
    "label.time": "Time",
    "label.created": "Created",
    "label.updated": "Updated",
    "label.actions": "Actions",
    "label.all": "All",
    "label.none": "None",
    "label.yes": "Yes",
    "label.no": "No",
    "label.active": "Active",
    "label.inactive": "Inactive",
    "label.enabled": "Enabled",
    "label.disabled": "Disabled",
    "label.pending": "Pending",
    "label.language": "Language",
    "label.select_language": "Select Language",
    "label.date_range": "Date Range",
    "label.from": "From",
    "label.to": "To",
    "label.total": "Total",
    "label.size": "Size",
    "label.format": "Format",
    
    // Login Page
    "login.title": "Sign in to your account",
    "login.subtitle": "Welcome back! Please enter your details.",
    "login.email_placeholder": "Enter your email",
    "login.password_placeholder": "Enter your password",
    "login.remember_me": "Remember me",
    "login.forgot_password": "Forgot password?",
    "login.no_account": "Don't have an account?",
    "login.sign_up": "Sign up",
    "login.or_continue": "Or continue with",
    
    // Tenant Onboarding - Organization Details
    "tenant.org_name": "Organization Name",
    "tenant.org_type": "Organization Type",
    "tenant.industry": "Industry",
    "tenant.company_size": "Company Size",
    "tenant.country": "Country",
    "tenant.timezone": "Timezone",
    "tenant.primary_contact": "Primary Contact",
    "tenant.billing_email": "Billing Email",
    
    // Industry Options
    "industry.technology": "Technology",
    "industry.finance": "Finance & Banking",
    "industry.healthcare": "Healthcare",
    "industry.government": "Government",
    "industry.defense": "Defense & Aerospace",
    "industry.telecommunications": "Telecommunications",
    "industry.energy": "Energy & Utilities",
    "industry.manufacturing": "Manufacturing",
    "industry.retail": "Retail & E-commerce",
    "industry.education": "Education",
    "industry.media": "Media & Entertainment",
    "industry.gaming": "Gaming & Gambling",
    "industry.automotive": "Automotive",
    "industry.research": "Research & Academia",
    "industry.other": "Other",
    
    // Organization Type Options
    "org_type.enterprise": "Enterprise",
    "org_type.startup": "Startup",
    "org_type.smb": "Small/Medium Business",
    "org_type.government": "Government Agency",
    "org_type.nonprofit": "Non-Profit",
    "org_type.educational": "Educational Institution",
    
    // Company Size Options
    "company_size.1-10": "1-10 employees",
    "company_size.11-50": "11-50 employees",
    "company_size.51-200": "51-200 employees",
    "company_size.201-500": "201-500 employees",
    "company_size.501-1000": "501-1000 employees",
    "company_size.1001-5000": "1001-5000 employees",
    "company_size.5000+": "5000+ employees",
    
    // Widget Builder
    "widget.title": "Widget Builder",
    "widget.subtitle": "Create and manage dashboard widgets with custom SQL queries",
    "widget.create": "Create Widget",
    "widget.search": "Search widgets...",
    "widget.type_filter": "Filter by type",
    "widget.all_types": "All Types",
    "widget.kpi_card": "KPI Card",
    "widget.line_chart": "Line Chart",
    "widget.bar_chart": "Bar Chart",
    "widget.pie_chart": "Pie Chart",
    "widget.gauge": "Gauge",
    "widget.data_table": "Data Table",
    "widget.refresh_interval": "Refresh Interval",
    "widget.chart_type": "Chart Type",
    "widget.sql_editor": "SQL Editor",
    "widget.templates": "Templates",
    "widget.schema": "Schema",
    "widget.preview": "Preview",
    "widget.publish": "Publish Widget",
    "widget.save_draft": "Save as Draft",
    
    // Role Management
    "role.title": "Role Management",
    "role.subtitle": "Define roles and manage permissions for platform access control",
    "role.create": "Create Custom Role",
    "role.search": "Search roles...",
    "role.system_roles": "System Roles",
    "role.custom_roles": "Custom Roles",
    "role.permissions": "Permissions",
    "role.members": "members",
    "role.copy_from": "Copy Permissions From",
    "role.start_scratch": "Start from scratch",
    
    // Report Builder
    "report.title": "Report Builder",
    "report.subtitle": "Create and manage reports with charts, tables, and drill-down capabilities",
    "report.create": "Create Report",
    "report.search": "Search reports...",
    "report.chart_report": "Chart Report",
    "report.tabular_report": "Tabular Report",
    "report.drilldown_report": "Drill-Down Report",
    "report.schedule_report": "Schedule Report",
    "report.run_now": "Run Now",
    "report.last_run": "Last Run",
    "report.next_run": "Next Run",
    "report.output_format": "Output Format",
    "report.parameters": "Parameters",
    "report.recipients": "Recipients",
    "report.frequency": "Frequency",
    "report.daily": "Daily",
    "report.weekly": "Weekly",
    "report.monthly": "Monthly",
    "report.custom": "Custom",
    
    // Data Archival
    "archival.title": "Data Archival Management",
    "archival.subtitle": "Manage data retention policies, archive jobs, and retrieval requests",
    "archival.active_data": "Active Data",
    "archival.archived_data": "Archived Data",
    "archival.retention_policy": "Retention Policy",
    "archival.retention_years": "years retention",
    "archival.archive_jobs": "Archive Jobs",
    "archival.retrieval_requests": "Retrieval Requests",
    "archival.run_archive": "Run Archive Job",
    "archival.request_data": "Request Archived Data",
    "archival.job_status": "Job Status",
    "archival.completed": "Completed",
    "archival.in_progress": "In Progress",
    "archival.failed": "Failed",
    "archival.scheduled": "Scheduled",
    "archival.records_archived": "Records Archived",
    "archival.storage_saved": "Storage Saved",
    "archival.data_type": "Data Type",
    "archival.date_range": "Date Range",
    "archival.download_link": "Download Link",
    "archival.expires_in": "Expires in",
    "archival.transactional": "Transactional Data",
    "archival.master": "Master Data",
    
    // Billing
    "billing.title": "Billing & Plans",
    "billing.subtitle": "Manage your subscription, payment methods, and view invoices",
    "billing.current_plan": "Current Plan",
    "billing.upgrade": "Upgrade Plan",
    "billing.downgrade": "Downgrade",
    "billing.payment_method": "Payment Method",
    "billing.add_payment": "Add Payment Method",
    "billing.invoices": "Invoices",
    "billing.usage": "Usage",
    "billing.api_calls": "API Calls",
    "billing.bandwidth": "Bandwidth",
    "billing.overage": "Overage Charges",
    
    // Status Messages
    "status.all_systems_operational": "All Systems Operational",
    "status.loading": "Loading...",
    "status.error": "An error occurred",
    "status.success": "Success",
    "status.saving": "Saving...",
    "status.saved": "Saved",
    
    // Translation Management
    "translation.title": "Translation Management",
    "translation.subtitle": "Manage translations for all supported languages",
    "translation.bulk_upload": "Bulk Upload",
    "translation.export_all": "Export All",
    "translation.add_key": "Add Translation Key",
    "translation.key": "Translation Key",
    "translation.base_value": "Base Value (English)",
    "translation.translation": "Translation",
    "translation.missing": "Missing",
    "translation.translated": "Translated",
    "translation.upload_json": "Upload JSON",
    "translation.upload_csv": "Upload CSV",
    "translation.download_template": "Download Template",
    // Validation Messages
    "validation.email_required": "Email is required",
    "validation.password_required": "Password is required",
    "validation.email_invalid": "Please enter a valid email address",
    "validation.field_required": "This field is required",
  },
  
  fr: {
    // Navigation & Layout
    "nav.dashboard": "Tableau de bord",
    "nav.api_keys": "Clés API",
    "nav.analytics": "Analytique",
    "nav.billing": "Facturation et forfaits",
    "nav.api_docs": "Documentation API",
    "nav.settings": "Paramètres",
    "nav.admin_portal": "Portail Admin",
    "nav.customer_portal": "Portail Client",
    "nav.system_overview": "Aperçu du système",
    "nav.device_management": "Gestion des appareils",
    "nav.tenant_management": "Gestion des locataires",
    "nav.audit_logs": "Journaux d'audit",
    "nav.alerts": "Alertes",
    "nav.widget_builder": "Constructeur de widgets",
    "nav.role_management": "Gestion des rôles",
    "nav.translations": "Traductions",
    "nav.report_builder": "Générateur de rapports",
    "nav.reports": "Rapports",
    "nav.data_archival": "Archivage des données",
    
    // Common Actions
    "action.save": "Enregistrer",
    "action.cancel": "Annuler",
    "action.delete": "Supprimer",
    "action.edit": "Modifier",
    "action.create": "Créer",
    "action.search": "Rechercher",
    "action.filter": "Filtrer",
    "action.export": "Exporter",
    "action.import": "Importer",
    "action.upload": "Télécharger",
    "action.download": "Télécharger",
    "action.submit": "Soumettre",
    "action.next": "Suivant",
    "action.previous": "Précédent",
    "action.back": "Retour",
    "action.close": "Fermer",
    "action.confirm": "Confirmer",
    "action.add": "Ajouter",
    "action.remove": "Retirer",
    "action.view": "Voir",
    "action.copy": "Copier",
    "action.duplicate": "Dupliquer",
    "action.refresh": "Actualiser",
    "action.run": "Exécuter",
    "action.schedule": "Planifier",
    "action.archive": "Archiver",
    "action.restore": "Restaurer",
    "action.request": "Demander",
    "action.login": "Se connecter",
    "action.logout": "Se déconnecter",
    "action.register": "S'inscrire",
    
    // Common Labels
    "label.name": "Nom",
    "label.email": "Email",
    "label.password": "Mot de passe",
    "label.description": "Description",
    "label.status": "Statut",
    "label.type": "Type",
    "label.date": "Date",
    "label.time": "Heure",
    "label.created": "Créé",
    "label.updated": "Mis à jour",
    "label.actions": "Actions",
    "label.all": "Tous",
    "label.none": "Aucun",
    "label.yes": "Oui",
    "label.no": "Non",
    "label.active": "Actif",
    "label.inactive": "Inactif",
    "label.enabled": "Activé",
    "label.disabled": "Désactivé",
    "label.pending": "En attente",
    "label.language": "Langue",
    "label.select_language": "Sélectionner la langue",
    "label.date_range": "Plage de dates",
    "label.from": "De",
    "label.to": "À",
    "label.total": "Total",
    "label.size": "Taille",
    "label.format": "Format",
    
    // Login Page
    "login.title": "Connectez-vous à votre compte",
    "login.subtitle": "Bienvenue ! Veuillez entrer vos informations.",
    "login.email_placeholder": "Entrez votre email",
    "login.password_placeholder": "Entrez votre mot de passe",
    "login.remember_me": "Se souvenir de moi",
    "login.forgot_password": "Mot de passe oublié ?",
    "login.no_account": "Vous n'avez pas de compte ?",
    "login.sign_up": "S'inscrire",
    "login.or_continue": "Ou continuer avec",
    
    // Tenant Onboarding
    "tenant.org_name": "Nom de l'organisation",
    "tenant.org_type": "Type d'organisation",
    "tenant.industry": "Secteur d'activité",
    "tenant.company_size": "Taille de l'entreprise",
    "tenant.country": "Pays",
    "tenant.timezone": "Fuseau horaire",
    "tenant.primary_contact": "Contact principal",
    "tenant.billing_email": "Email de facturation",
    
    // Industry Options
    "industry.technology": "Technologie",
    "industry.finance": "Finance et Banque",
    "industry.healthcare": "Santé",
    "industry.government": "Gouvernement",
    "industry.defense": "Défense et Aérospatiale",
    "industry.telecommunications": "Télécommunications",
    "industry.energy": "Énergie et Services publics",
    "industry.manufacturing": "Fabrication",
    "industry.retail": "Commerce de détail",
    "industry.education": "Éducation",
    "industry.media": "Médias et Divertissement",
    "industry.gaming": "Jeux et Paris",
    "industry.automotive": "Automobile",
    "industry.research": "Recherche et Académie",
    "industry.other": "Autre",
    
    // Organization Type Options
    "org_type.enterprise": "Grande entreprise",
    "org_type.startup": "Startup",
    "org_type.smb": "PME",
    "org_type.government": "Agence gouvernementale",
    "org_type.nonprofit": "Organisation à but non lucratif",
    "org_type.educational": "Établissement d'enseignement",
    
    // Company Size Options
    "company_size.1-10": "1-10 employés",
    "company_size.11-50": "11-50 employés",
    "company_size.51-200": "51-200 employés",
    "company_size.201-500": "201-500 employés",
    "company_size.501-1000": "501-1000 employés",
    "company_size.1001-5000": "1001-5000 employés",
    "company_size.5000+": "5000+ employés",
    
    // Widget Builder
    "widget.title": "Constructeur de widgets",
    "widget.subtitle": "Créez et gérez des widgets de tableau de bord avec des requêtes SQL personnalisées",
    "widget.create": "Créer un widget",
    "widget.search": "Rechercher des widgets...",
    "widget.type_filter": "Filtrer par type",
    "widget.all_types": "Tous les types",
    "widget.kpi_card": "Carte KPI",
    "widget.line_chart": "Graphique en ligne",
    "widget.bar_chart": "Graphique à barres",
    "widget.pie_chart": "Graphique circulaire",
    "widget.gauge": "Jauge",
    "widget.data_table": "Tableau de données",
    "widget.refresh_interval": "Intervalle de rafraîchissement",
    "widget.chart_type": "Type de graphique",
    "widget.sql_editor": "Éditeur SQL",
    "widget.templates": "Modèles",
    "widget.schema": "Schéma",
    "widget.preview": "Aperçu",
    "widget.publish": "Publier le widget",
    "widget.save_draft": "Enregistrer comme brouillon",
    
    // Role Management
    "role.title": "Gestion des rôles",
    "role.subtitle": "Définissez les rôles et gérez les permissions pour le contrôle d'accès",
    "role.create": "Créer un rôle personnalisé",
    "role.search": "Rechercher des rôles...",
    "role.system_roles": "Rôles système",
    "role.custom_roles": "Rôles personnalisés",
    "role.permissions": "Permissions",
    "role.members": "membres",
    "role.copy_from": "Copier les permissions depuis",
    "role.start_scratch": "Partir de zéro",
    
    // Report Builder
    "report.title": "Générateur de rapports",
    "report.subtitle": "Créez et gérez des rapports avec graphiques, tableaux et exploration",
    "report.create": "Créer un rapport",
    "report.search": "Rechercher des rapports...",
    "report.chart_report": "Rapport graphique",
    "report.tabular_report": "Rapport tabulaire",
    "report.drilldown_report": "Rapport d'exploration",
    "report.schedule_report": "Planifier le rapport",
    "report.run_now": "Exécuter maintenant",
    "report.last_run": "Dernière exécution",
    "report.next_run": "Prochaine exécution",
    "report.output_format": "Format de sortie",
    "report.parameters": "Paramètres",
    "report.recipients": "Destinataires",
    "report.frequency": "Fréquence",
    "report.daily": "Quotidien",
    "report.weekly": "Hebdomadaire",
    "report.monthly": "Mensuel",
    "report.custom": "Personnalisé",
    
    // Data Archival
    "archival.title": "Gestion de l'archivage des données",
    "archival.subtitle": "Gérez les politiques de rétention, les tâches d'archivage et les demandes de récupération",
    "archival.active_data": "Données actives",
    "archival.archived_data": "Données archivées",
    "archival.retention_policy": "Politique de rétention",
    "archival.retention_years": "ans de rétention",
    "archival.archive_jobs": "Tâches d'archivage",
    "archival.retrieval_requests": "Demandes de récupération",
    "archival.run_archive": "Exécuter l'archivage",
    "archival.request_data": "Demander des données archivées",
    "archival.job_status": "Statut de la tâche",
    "archival.completed": "Terminé",
    "archival.in_progress": "En cours",
    "archival.failed": "Échoué",
    "archival.scheduled": "Planifié",
    "archival.records_archived": "Enregistrements archivés",
    "archival.storage_saved": "Stockage économisé",
    "archival.data_type": "Type de données",
    "archival.date_range": "Plage de dates",
    "archival.download_link": "Lien de téléchargement",
    "archival.expires_in": "Expire dans",
    "archival.transactional": "Données transactionnelles",
    "archival.master": "Données de référence",
    
    // Billing
    "billing.title": "Facturation et forfaits",
    "billing.subtitle": "Gérez votre abonnement, vos moyens de paiement et consultez vos factures",
    "billing.current_plan": "Forfait actuel",
    "billing.upgrade": "Mettre à niveau",
    "billing.downgrade": "Rétrograder",
    "billing.payment_method": "Moyen de paiement",
    "billing.add_payment": "Ajouter un moyen de paiement",
    "billing.invoices": "Factures",
    "billing.usage": "Utilisation",
    "billing.api_calls": "Appels API",
    "billing.bandwidth": "Bande passante",
    "billing.overage": "Frais de dépassement",
    
    // Status Messages
    "status.all_systems_operational": "Tous les systèmes opérationnels",
    "status.loading": "Chargement...",
    "status.error": "Une erreur s'est produite",
    "status.success": "Succès",
    "status.saving": "Enregistrement...",
    "status.saved": "Enregistré",
    
    // Translation Management
    "translation.title": "Gestion des traductions",
    "translation.subtitle": "Gérez les traductions pour toutes les langues prises en charge",
    "translation.bulk_upload": "Téléchargement en masse",
    "translation.export_all": "Tout exporter",
    "translation.add_key": "Ajouter une clé",
    "translation.key": "Clé de traduction",
    "translation.base_value": "Valeur de base (Anglais)",
    "translation.translation": "Traduction",
    "translation.missing": "Manquant",
    "translation.translated": "Traduit",
    "translation.upload_json": "Télécharger JSON",
    "translation.upload_csv": "Télécharger CSV",
    "translation.download_template": "Télécharger le modèle",
    // Validation Messages
    "validation.email_required": "L'adresse email est requise",
    "validation.password_required": "Le mot de passe est requis",
    "validation.email_invalid": "Veuillez saisir une adresse email valide",
    "validation.field_required": "Ce champ est obligatoire",
  },
  
  es: {
    // Navigation & Layout
    "nav.dashboard": "Panel de control",
    "nav.api_keys": "Claves API",
    "nav.analytics": "Análisis",
    "nav.billing": "Facturación y planes",
    "nav.api_docs": "Documentación API",
    "nav.settings": "Configuración",
    "nav.admin_portal": "Portal de administración",
    "nav.customer_portal": "Portal del cliente",
    "nav.system_overview": "Resumen del sistema",
    "nav.device_management": "Gestión de dispositivos",
    "nav.tenant_management": "Gestión de inquilinos",
    "nav.audit_logs": "Registros de auditoría",
    "nav.alerts": "Alertas",
    "nav.widget_builder": "Constructor de widgets",
    "nav.role_management": "Gestión de roles",
    "nav.translations": "Traducciones",
    "nav.report_builder": "Generador de informes",
    "nav.reports": "Informes",
    "nav.data_archival": "Archivado de datos",
    
    // Common Actions
    "action.save": "Guardar",
    "action.cancel": "Cancelar",
    "action.delete": "Eliminar",
    "action.edit": "Editar",
    "action.create": "Crear",
    "action.search": "Buscar",
    "action.filter": "Filtrar",
    "action.export": "Exportar",
    "action.import": "Importar",
    "action.upload": "Subir",
    "action.download": "Descargar",
    "action.submit": "Enviar",
    "action.next": "Siguiente",
    "action.previous": "Anterior",
    "action.back": "Volver",
    "action.close": "Cerrar",
    "action.confirm": "Confirmar",
    "action.add": "Añadir",
    "action.remove": "Eliminar",
    "action.view": "Ver",
    "action.copy": "Copiar",
    "action.duplicate": "Duplicar",
    "action.refresh": "Actualizar",
    "action.run": "Ejecutar",
    "action.schedule": "Programar",
    "action.archive": "Archivar",
    "action.restore": "Restaurar",
    "action.request": "Solicitar",
    "action.login": "Iniciar sesión",
    "action.logout": "Cerrar sesión",
    "action.register": "Registrarse",
    
    // Common Labels
    "label.name": "Nombre",
    "label.email": "Correo electrónico",
    "label.password": "Contraseña",
    "label.description": "Descripción",
    "label.status": "Estado",
    "label.type": "Tipo",
    "label.date": "Fecha",
    "label.time": "Hora",
    "label.created": "Creado",
    "label.updated": "Actualizado",
    "label.actions": "Acciones",
    "label.all": "Todos",
    "label.none": "Ninguno",
    "label.yes": "Sí",
    "label.no": "No",
    "label.active": "Activo",
    "label.inactive": "Inactivo",
    "label.enabled": "Habilitado",
    "label.disabled": "Deshabilitado",
    "label.pending": "Pendiente",
    "label.language": "Idioma",
    "label.select_language": "Seleccionar idioma",
    "label.date_range": "Rango de fechas",
    "label.from": "Desde",
    "label.to": "Hasta",
    "label.total": "Total",
    "label.size": "Tamaño",
    "label.format": "Formato",
    
    // Login Page
    "login.title": "Inicia sesión en tu cuenta",
    "login.subtitle": "¡Bienvenido! Por favor ingresa tus datos.",
    "login.email_placeholder": "Ingresa tu correo electrónico",
    "login.password_placeholder": "Ingresa tu contraseña",
    "login.remember_me": "Recordarme",
    "login.forgot_password": "¿Olvidaste tu contraseña?",
    "login.no_account": "¿No tienes una cuenta?",
    "login.sign_up": "Regístrate",
    "login.or_continue": "O continúa con",
    
    // Tenant Onboarding
    "tenant.org_name": "Nombre de la organización",
    "tenant.org_type": "Tipo de organización",
    "tenant.industry": "Industria",
    "tenant.company_size": "Tamaño de la empresa",
    "tenant.country": "País",
    "tenant.timezone": "Zona horaria",
    "tenant.primary_contact": "Contacto principal",
    "tenant.billing_email": "Correo de facturación",
    
    // Industry Options
    "industry.technology": "Tecnología",
    "industry.finance": "Finanzas y Banca",
    "industry.healthcare": "Salud",
    "industry.government": "Gobierno",
    "industry.defense": "Defensa y Aeroespacial",
    "industry.telecommunications": "Telecomunicaciones",
    "industry.energy": "Energía y Servicios públicos",
    "industry.manufacturing": "Manufactura",
    "industry.retail": "Comercio minorista",
    "industry.education": "Educación",
    "industry.media": "Medios y Entretenimiento",
    "industry.gaming": "Juegos y Apuestas",
    "industry.automotive": "Automotriz",
    "industry.research": "Investigación y Academia",
    "industry.other": "Otro",
    
    // Organization Type Options
    "org_type.enterprise": "Empresa grande",
    "org_type.startup": "Startup",
    "org_type.smb": "PYME",
    "org_type.government": "Agencia gubernamental",
    "org_type.nonprofit": "Organización sin fines de lucro",
    "org_type.educational": "Institución educativa",
    
    // Company Size Options
    "company_size.1-10": "1-10 empleados",
    "company_size.11-50": "11-50 empleados",
    "company_size.51-200": "51-200 empleados",
    "company_size.201-500": "201-500 empleados",
    "company_size.501-1000": "501-1000 empleados",
    "company_size.1001-5000": "1001-5000 empleados",
    "company_size.5000+": "5000+ empleados",
    
    // Widget Builder
    "widget.title": "Constructor de widgets",
    "widget.subtitle": "Crea y gestiona widgets de panel con consultas SQL personalizadas",
    "widget.create": "Crear widget",
    "widget.search": "Buscar widgets...",
    "widget.type_filter": "Filtrar por tipo",
    "widget.all_types": "Todos los tipos",
    "widget.kpi_card": "Tarjeta KPI",
    "widget.line_chart": "Gráfico de líneas",
    "widget.bar_chart": "Gráfico de barras",
    "widget.pie_chart": "Gráfico circular",
    "widget.gauge": "Indicador",
    "widget.data_table": "Tabla de datos",
    "widget.refresh_interval": "Intervalo de actualización",
    "widget.chart_type": "Tipo de gráfico",
    "widget.sql_editor": "Editor SQL",
    "widget.templates": "Plantillas",
    "widget.schema": "Esquema",
    "widget.preview": "Vista previa",
    "widget.publish": "Publicar widget",
    "widget.save_draft": "Guardar como borrador",
    
    // Role Management
    "role.title": "Gestión de roles",
    "role.subtitle": "Define roles y gestiona permisos para el control de acceso",
    "role.create": "Crear rol personalizado",
    "role.search": "Buscar roles...",
    "role.system_roles": "Roles del sistema",
    "role.custom_roles": "Roles personalizados",
    "role.permissions": "Permisos",
    "role.members": "miembros",
    "role.copy_from": "Copiar permisos desde",
    "role.start_scratch": "Comenzar desde cero",
    
    // Report Builder
    "report.title": "Generador de informes",
    "report.subtitle": "Crea y gestiona informes con gráficos, tablas y exploración",
    "report.create": "Crear informe",
    "report.search": "Buscar informes...",
    "report.chart_report": "Informe gráfico",
    "report.tabular_report": "Informe tabular",
    "report.drilldown_report": "Informe de exploración",
    "report.schedule_report": "Programar informe",
    "report.run_now": "Ejecutar ahora",
    "report.last_run": "Última ejecución",
    "report.next_run": "Próxima ejecución",
    "report.output_format": "Formato de salida",
    "report.parameters": "Parámetros",
    "report.recipients": "Destinatarios",
    "report.frequency": "Frecuencia",
    "report.daily": "Diario",
    "report.weekly": "Semanal",
    "report.monthly": "Mensual",
    "report.custom": "Personalizado",
    
    // Data Archival
    "archival.title": "Gestión de archivado de datos",
    "archival.subtitle": "Gestiona políticas de retención, trabajos de archivo y solicitudes de recuperación",
    "archival.active_data": "Datos activos",
    "archival.archived_data": "Datos archivados",
    "archival.retention_policy": "Política de retención",
    "archival.retention_years": "años de retención",
    "archival.archive_jobs": "Trabajos de archivo",
    "archival.retrieval_requests": "Solicitudes de recuperación",
    "archival.run_archive": "Ejecutar archivado",
    "archival.request_data": "Solicitar datos archivados",
    "archival.job_status": "Estado del trabajo",
    "archival.completed": "Completado",
    "archival.in_progress": "En progreso",
    "archival.failed": "Fallido",
    "archival.scheduled": "Programado",
    "archival.records_archived": "Registros archivados",
    "archival.storage_saved": "Almacenamiento ahorrado",
    "archival.data_type": "Tipo de datos",
    "archival.date_range": "Rango de fechas",
    "archival.download_link": "Enlace de descarga",
    "archival.expires_in": "Expira en",
    "archival.transactional": "Datos transaccionales",
    "archival.master": "Datos maestros",
    
    // Billing
    "billing.title": "Facturación y planes",
    "billing.subtitle": "Gestiona tu suscripción, métodos de pago y consulta facturas",
    "billing.current_plan": "Plan actual",
    "billing.upgrade": "Mejorar plan",
    "billing.downgrade": "Degradar",
    "billing.payment_method": "Método de pago",
    "billing.add_payment": "Añadir método de pago",
    "billing.invoices": "Facturas",
    "billing.usage": "Uso",
    "billing.api_calls": "Llamadas API",
    "billing.bandwidth": "Ancho de banda",
    "billing.overage": "Cargos por exceso",
    
    // Status Messages
    "status.all_systems_operational": "Todos los sistemas operativos",
    "status.loading": "Cargando...",
    "status.error": "Ocurrió un error",
    "status.success": "Éxito",
    "status.saving": "Guardando...",
    "status.saved": "Guardado",
    
    // Translation Management
    "translation.title": "Gestión de traducciones",
    "translation.subtitle": "Gestiona las traducciones para todos los idiomas admitidos",
    "translation.bulk_upload": "Carga masiva",
    "translation.export_all": "Exportar todo",
    "translation.add_key": "Añadir clave",
    "translation.key": "Clave de traducción",
    "translation.base_value": "Valor base (Inglés)",
    "translation.translation": "Traducción",
    "translation.missing": "Faltante",
    "translation.translated": "Traducido",
    "translation.upload_json": "Subir JSON",
    "translation.upload_csv": "Subir CSV",
    "translation.download_template": "Descargar plantilla",
    // Validation Messages
    "validation.email_required": "El correo electrónico es obligatorio",
    "validation.password_required": "La contraseña es obligatoria",
    "validation.email_invalid": "Por favor, introduce una dirección de correo válida",
    "validation.field_required": "Este campo es obligatorio",
  },
  
  de: {
    // Navigation & Layout
    "nav.dashboard": "Dashboard",
    "nav.api_keys": "API-Schlüssel",
    "nav.analytics": "Analytik",
    "nav.billing": "Abrechnung & Pläne",
    "nav.api_docs": "API-Dokumentation",
    "nav.settings": "Einstellungen",
    "nav.admin_portal": "Admin-Portal",
    "nav.customer_portal": "Kundenportal",
    "nav.system_overview": "Systemübersicht",
    "nav.device_management": "Geräteverwaltung",
    "nav.tenant_management": "Mandantenverwaltung",
    "nav.audit_logs": "Prüfprotokolle",
    "nav.alerts": "Warnungen",
    "nav.widget_builder": "Widget-Builder",
    "nav.role_management": "Rollenverwaltung",
    "nav.translations": "Übersetzungen",
    "nav.report_builder": "Berichtsgenerator",
    "nav.reports": "Berichte",
    "nav.data_archival": "Datenarchivierung",
    
    // Common Actions
    "action.save": "Speichern",
    "action.cancel": "Abbrechen",
    "action.delete": "Löschen",
    "action.edit": "Bearbeiten",
    "action.create": "Erstellen",
    "action.search": "Suchen",
    "action.filter": "Filtern",
    "action.export": "Exportieren",
    "action.import": "Importieren",
    "action.upload": "Hochladen",
    "action.download": "Herunterladen",
    "action.submit": "Absenden",
    "action.next": "Weiter",
    "action.previous": "Zurück",
    "action.back": "Zurück",
    "action.close": "Schließen",
    "action.confirm": "Bestätigen",
    "action.add": "Hinzufügen",
    "action.remove": "Entfernen",
    "action.view": "Ansehen",
    "action.copy": "Kopieren",
    "action.duplicate": "Duplizieren",
    "action.refresh": "Aktualisieren",
    "action.run": "Ausführen",
    "action.schedule": "Planen",
    "action.archive": "Archivieren",
    "action.restore": "Wiederherstellen",
    "action.request": "Anfordern",
    "action.login": "Anmelden",
    "action.logout": "Abmelden",
    "action.register": "Registrieren",
    
    // Common Labels
    "label.name": "Name",
    "label.email": "E-Mail",
    "label.password": "Passwort",
    "label.description": "Beschreibung",
    "label.status": "Status",
    "label.type": "Typ",
    "label.date": "Datum",
    "label.time": "Zeit",
    "label.created": "Erstellt",
    "label.updated": "Aktualisiert",
    "label.actions": "Aktionen",
    "label.all": "Alle",
    "label.none": "Keine",
    "label.yes": "Ja",
    "label.no": "Nein",
    "label.active": "Aktiv",
    "label.inactive": "Inaktiv",
    "label.enabled": "Aktiviert",
    "label.disabled": "Deaktiviert",
    "label.pending": "Ausstehend",
    "label.language": "Sprache",
    "label.select_language": "Sprache auswählen",
    "label.date_range": "Datumsbereich",
    "label.from": "Von",
    "label.to": "Bis",
    "label.total": "Gesamt",
    "label.size": "Größe",
    "label.format": "Format",
    
    // Login Page
    "login.title": "Bei Ihrem Konto anmelden",
    "login.subtitle": "Willkommen zurück! Bitte geben Sie Ihre Daten ein.",
    "login.email_placeholder": "E-Mail eingeben",
    "login.password_placeholder": "Passwort eingeben",
    "login.remember_me": "Angemeldet bleiben",
    "login.forgot_password": "Passwort vergessen?",
    "login.no_account": "Noch kein Konto?",
    "login.sign_up": "Registrieren",
    "login.or_continue": "Oder fortfahren mit",
    
    // Tenant Onboarding
    "tenant.org_name": "Organisationsname",
    "tenant.org_type": "Organisationstyp",
    "tenant.industry": "Branche",
    "tenant.company_size": "Unternehmensgröße",
    "tenant.country": "Land",
    "tenant.timezone": "Zeitzone",
    "tenant.primary_contact": "Hauptansprechpartner",
    "tenant.billing_email": "Rechnungs-E-Mail",
    
    // Industry Options
    "industry.technology": "Technologie",
    "industry.finance": "Finanzen & Bankwesen",
    "industry.healthcare": "Gesundheitswesen",
    "industry.government": "Regierung",
    "industry.defense": "Verteidigung & Luft- und Raumfahrt",
    "industry.telecommunications": "Telekommunikation",
    "industry.energy": "Energie & Versorgung",
    "industry.manufacturing": "Fertigung",
    "industry.retail": "Einzelhandel & E-Commerce",
    "industry.education": "Bildung",
    "industry.media": "Medien & Unterhaltung",
    "industry.gaming": "Spiele & Glücksspiel",
    "industry.automotive": "Automobil",
    "industry.research": "Forschung & Wissenschaft",
    "industry.other": "Andere",
    
    // Organization Type Options
    "org_type.enterprise": "Großunternehmen",
    "org_type.startup": "Startup",
    "org_type.smb": "KMU",
    "org_type.government": "Regierungsbehörde",
    "org_type.nonprofit": "Gemeinnützige Organisation",
    "org_type.educational": "Bildungseinrichtung",
    
    // Company Size Options
    "company_size.1-10": "1-10 Mitarbeiter",
    "company_size.11-50": "11-50 Mitarbeiter",
    "company_size.51-200": "51-200 Mitarbeiter",
    "company_size.201-500": "201-500 Mitarbeiter",
    "company_size.501-1000": "501-1000 Mitarbeiter",
    "company_size.1001-5000": "1001-5000 Mitarbeiter",
    "company_size.5000+": "5000+ Mitarbeiter",
    
    // Widget Builder
    "widget.title": "Widget-Builder",
    "widget.subtitle": "Erstellen und verwalten Sie Dashboard-Widgets mit benutzerdefinierten SQL-Abfragen",
    "widget.create": "Widget erstellen",
    "widget.search": "Widgets suchen...",
    "widget.type_filter": "Nach Typ filtern",
    "widget.all_types": "Alle Typen",
    "widget.kpi_card": "KPI-Karte",
    "widget.line_chart": "Liniendiagramm",
    "widget.bar_chart": "Balkendiagramm",
    "widget.pie_chart": "Kreisdiagramm",
    "widget.gauge": "Messgerät",
    "widget.data_table": "Datentabelle",
    "widget.refresh_interval": "Aktualisierungsintervall",
    "widget.chart_type": "Diagrammtyp",
    "widget.sql_editor": "SQL-Editor",
    "widget.templates": "Vorlagen",
    "widget.schema": "Schema",
    "widget.preview": "Vorschau",
    "widget.publish": "Widget veröffentlichen",
    "widget.save_draft": "Als Entwurf speichern",
    
    // Role Management
    "role.title": "Rollenverwaltung",
    "role.subtitle": "Definieren Sie Rollen und verwalten Sie Berechtigungen für die Zugriffskontrolle",
    "role.create": "Benutzerdefinierte Rolle erstellen",
    "role.search": "Rollen suchen...",
    "role.system_roles": "Systemrollen",
    "role.custom_roles": "Benutzerdefinierte Rollen",
    "role.permissions": "Berechtigungen",
    "role.members": "Mitglieder",
    "role.copy_from": "Berechtigungen kopieren von",
    "role.start_scratch": "Von vorne beginnen",
    
    // Report Builder
    "report.title": "Berichtsgenerator",
    "report.subtitle": "Erstellen und verwalten Sie Berichte mit Diagrammen, Tabellen und Drill-Down",
    "report.create": "Bericht erstellen",
    "report.search": "Berichte suchen...",
    "report.chart_report": "Diagrammbericht",
    "report.tabular_report": "Tabellenbericht",
    "report.drilldown_report": "Drill-Down-Bericht",
    "report.schedule_report": "Bericht planen",
    "report.run_now": "Jetzt ausführen",
    "report.last_run": "Letzte Ausführung",
    "report.next_run": "Nächste Ausführung",
    "report.output_format": "Ausgabeformat",
    "report.parameters": "Parameter",
    "report.recipients": "Empfänger",
    "report.frequency": "Häufigkeit",
    "report.daily": "Täglich",
    "report.weekly": "Wöchentlich",
    "report.monthly": "Monatlich",
    "report.custom": "Benutzerdefiniert",
    
    // Data Archival
    "archival.title": "Datenarchivierungsverwaltung",
    "archival.subtitle": "Verwalten Sie Aufbewahrungsrichtlinien, Archivierungsaufträge und Abrufanfragen",
    "archival.active_data": "Aktive Daten",
    "archival.archived_data": "Archivierte Daten",
    "archival.retention_policy": "Aufbewahrungsrichtlinie",
    "archival.retention_years": "Jahre Aufbewahrung",
    "archival.archive_jobs": "Archivierungsaufträge",
    "archival.retrieval_requests": "Abrufanfragen",
    "archival.run_archive": "Archivierung ausführen",
    "archival.request_data": "Archivierte Daten anfordern",
    "archival.job_status": "Auftragsstatus",
    "archival.completed": "Abgeschlossen",
    "archival.in_progress": "In Bearbeitung",
    "archival.failed": "Fehlgeschlagen",
    "archival.scheduled": "Geplant",
    "archival.records_archived": "Archivierte Datensätze",
    "archival.storage_saved": "Eingesparter Speicher",
    "archival.data_type": "Datentyp",
    "archival.date_range": "Datumsbereich",
    "archival.download_link": "Download-Link",
    "archival.expires_in": "Läuft ab in",
    "archival.transactional": "Transaktionsdaten",
    "archival.master": "Stammdaten",
    
    // Billing
    "billing.title": "Abrechnung & Pläne",
    "billing.subtitle": "Verwalten Sie Ihr Abonnement, Zahlungsmethoden und sehen Sie Rechnungen ein",
    "billing.current_plan": "Aktueller Plan",
    "billing.upgrade": "Upgrade",
    "billing.downgrade": "Downgrade",
    "billing.payment_method": "Zahlungsmethode",
    "billing.add_payment": "Zahlungsmethode hinzufügen",
    "billing.invoices": "Rechnungen",
    "billing.usage": "Nutzung",
    "billing.api_calls": "API-Aufrufe",
    "billing.bandwidth": "Bandbreite",
    "billing.overage": "Überschreitungsgebühren",
    
    // Status Messages
    "status.all_systems_operational": "Alle Systeme betriebsbereit",
    "status.loading": "Wird geladen...",
    "status.error": "Ein Fehler ist aufgetreten",
    "status.success": "Erfolg",
    "status.saving": "Wird gespeichert...",
    "status.saved": "Gespeichert",
    
    // Translation Management
    "translation.title": "Übersetzungsverwaltung",
    "translation.subtitle": "Verwalten Sie Übersetzungen für alle unterstützten Sprachen",
    "translation.bulk_upload": "Massen-Upload",
    "translation.export_all": "Alles exportieren",
    "translation.add_key": "Schlüssel hinzufügen",
    "translation.key": "Übersetzungsschlüssel",
    "translation.base_value": "Basiswert (Englisch)",
    "translation.translation": "Übersetzung",
    "translation.missing": "Fehlend",
    "translation.translated": "Übersetzt",
    "translation.upload_json": "JSON hochladen",
    "translation.upload_csv": "CSV hochladen",
    "translation.download_template": "Vorlage herunterladen",
    // Validation Messages
    "validation.email_required": "E-Mail-Adresse ist erforderlich",
    "validation.password_required": "Passwort ist erforderlich",
    "validation.email_invalid": "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    "validation.field_required": "Dieses Feld ist erforderlich",
  },
  
  hi: {
    "nav.dashboard": "डैशबोर्ड",
    "nav.settings": "सेटिंग्स",
    "nav.api_keys": "API कुंजियां",
    "action.save": "सहेजें",
    "action.cancel": "रद्द करें",
    "label.language": "भाषा",
  },
  
  ja: {
    "nav.dashboard": "ダッシュボード",
    "nav.settings": "設定",
    "nav.api_keys": "APIキー",
    "action.save": "保存",
    "action.cancel": "キャンセル",
    "label.language": "言語",
  },
  
  zh: {
    "nav.dashboard": "仪表板",
    "nav.settings": "设置",
    "nav.api_keys": "API密钥",
    "action.save": "保存",
    "action.cancel": "取消",
    "label.language": "语言",
  },
  
  ar: {
    "nav.dashboard": "لوحة التحكم",
    "nav.settings": "الإعدادات",
    "action.save": "حفظ",
    "action.cancel": "إلغاء",
    "label.language": "اللغة",
  },
  
  pt: {
    "nav.dashboard": "Painel",
    "nav.settings": "Configurações",
    "nav.api_keys": "Chaves API",
    "action.save": "Salvar",
    "action.cancel": "Cancelar",
    "label.language": "Idioma",
  },
  
  ko: {
    "nav.dashboard": "대시보드",
    "nav.settings": "설정",
    "nav.api_keys": "API 키",
    "action.save": "저장",
    "action.cancel": "취소",
    "label.language": "언어",
  },
};

const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English", flag: "US" },
  { code: "hi", name: "Hindi", nativeName: "हिंदी", flag: "IN" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "ES" },
  { code: "fr", name: "French", nativeName: "Français", flag: "FR" },
  { code: "de", name: "German", nativeName: "Deutsch", flag: "DE" },
  { code: "ja", name: "Japanese", nativeName: "日本語", flag: "JP" },
  { code: "zh", name: "Chinese", nativeName: "中文", flag: "CN" },
  { code: "ar", name: "Arabic", nativeName: "العربية", flag: "SA", rtl: true },
  { code: "pt", name: "Portuguese", nativeName: "Português", flag: "BR" },
  { code: "ko", name: "Korean", nativeName: "한국어", flag: "KR" },
];

const LanguageContext = createContext(null);

// Detect browser language
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0]; // Get 'en' from 'en-US'
  const supported = SUPPORTED_LANGUAGES.find(l => l.code === langCode);
  return supported ? langCode : 'en';
};

// Deep merge function to combine saved translations with defaults
const mergeTranslations = (defaults, saved) => {
  const result = {};
  // Start with all languages from defaults
  Object.keys(defaults).forEach(lang => {
    result[lang] = { ...defaults[lang] };
  });
  // Merge in saved translations (overwrites defaults where present)
  if (saved) {
    Object.keys(saved).forEach(lang => {
      result[lang] = { ...(result[lang] || {}), ...saved[lang] };
    });
  }
  return result;
};

export function LanguageProvider({ children }) {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Priority: 1. Saved preference, 2. Browser language, 3. Default (en)
    const saved = localStorage.getItem("qcore_language");
    if (saved) return saved;
    return detectBrowserLanguage();
  });
  
  const [translations, setTranslations] = useState(() => {
    const saved = localStorage.getItem("qcore_translations");
    // Always merge with defaults to ensure new keys are available
    return mergeTranslations(DEFAULT_TRANSLATIONS, saved ? JSON.parse(saved) : null);
  });

  // Save language preference
  useEffect(() => {
    localStorage.setItem("qcore_language", currentLanguage);
  }, [currentLanguage]);

  // Save translations
  useEffect(() => {
    localStorage.setItem("qcore_translations", JSON.stringify(translations));
  }, [translations]);

  // Translate function - checks translations then falls back to DEFAULT_TRANSLATIONS
  const t = useCallback((key, fallback = null) => {
    // First check current language in state translations
    const langTranslations = translations[currentLanguage] || {};
    if (langTranslations[key]) return langTranslations[key];
    
    // Fallback to English in state translations
    if (translations.en?.[key]) return translations.en[key];
    
    // Fallback to DEFAULT_TRANSLATIONS current language
    if (DEFAULT_TRANSLATIONS[currentLanguage]?.[key]) return DEFAULT_TRANSLATIONS[currentLanguage][key];
    
    // Fallback to DEFAULT_TRANSLATIONS English
    if (DEFAULT_TRANSLATIONS.en?.[key]) return DEFAULT_TRANSLATIONS.en[key];
    
    // Return fallback or key
    return fallback || key;
  }, [currentLanguage, translations]);

  // Update single translation
  const updateTranslation = useCallback((langCode, key, value) => {
    setTranslations(prev => ({
      ...prev,
      [langCode]: {
        ...(prev[langCode] || {}),
        [key]: value
      }
    }));
  }, []);

  // Bulk update translations for a language
  const bulkUpdateTranslations = useCallback((langCode, newTranslations) => {
    setTranslations(prev => ({
      ...prev,
      [langCode]: {
        ...(prev[langCode] || {}),
        ...newTranslations
      }
    }));
  }, []);

  // Add new translation key to all languages
  const addTranslationKey = useCallback((key, englishValue) => {
    setTranslations(prev => {
      const updated = { ...prev };
      updated.en = { ...(updated.en || {}), [key]: englishValue };
      return updated;
    });
  }, []);

  // Delete translation key from all languages
  const deleteTranslationKey = useCallback((key) => {
    setTranslations(prev => {
      const updated = {};
      Object.keys(prev).forEach(lang => {
        const langTrans = { ...prev[lang] };
        delete langTrans[key];
        updated[lang] = langTrans;
      });
      return updated;
    });
  }, []);

  // Import translations from JSON
  const importTranslations = useCallback((langCode, jsonData) => {
    try {
      const parsed = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      bulkUpdateTranslations(langCode, parsed);
      return { success: true, count: Object.keys(parsed).length };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, [bulkUpdateTranslations]);

  // Export translations for a language
  const exportTranslations = useCallback((langCode) => {
    return translations[langCode] || {};
  }, [translations]);

  // Get all translation keys
  const getAllKeys = useCallback(() => {
    const allKeys = new Set();
    Object.values(translations).forEach(langTrans => {
      Object.keys(langTrans).forEach(key => allKeys.add(key));
    });
    return Array.from(allKeys).sort();
  }, [translations]);

  // Get translation stats
  const getTranslationStats = useCallback((langCode) => {
    const allKeys = getAllKeys();
    const langTrans = translations[langCode] || {};
    const translated = allKeys.filter(key => langTrans[key]).length;
    return {
      total: allKeys.length,
      translated,
      missing: allKeys.length - translated,
      percentage: Math.round((translated / allKeys.length) * 100)
    };
  }, [translations, getAllKeys]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setTranslations(DEFAULT_TRANSLATIONS);
    localStorage.removeItem("qcore_translations");
  }, []);

  const value = {
    currentLanguage,
    setCurrentLanguage,
    t,
    translations,
    updateTranslation,
    bulkUpdateTranslations,
    addTranslationKey,
    deleteTranslationKey,
    importTranslations,
    exportTranslations,
    getAllKeys,
    getTranslationStats,
    resetToDefaults,
    supportedLanguages: SUPPORTED_LANGUAGES,
    detectBrowserLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export { SUPPORTED_LANGUAGES, DEFAULT_TRANSLATIONS, detectBrowserLanguage };
